import { apiFetchData, withAuthHeader, type AuthRetry } from '@/shared/lib/api';
import { config } from '@/shared/config';
import { useAuthStore } from '@/features/auth/store';
import type {
  RestaurantResponse,
  RestaurantCreateRequest,
  RestaurantUpdateRequest,
  DomainStatusResponse,
  DnsCheckResponse,
  DayHours,
} from './types';

const OWNER_RESTAURANTS = '/api/owner/restaurants';

function getAuthRetry(): AuthRetry {
  return {
    getToken: () => useAuthStore.getState().token,
    refreshTokens: () => useAuthStore.getState().refreshTokens(),
  };
}

/** Map frontend payload to backend: name->title, nest address, omit lat/lng, convert keywords to array, strip File objects */
function toBackendCreate(p: RestaurantCreateRequest) {
  const { name, latitude, longitude, logoFile, coverImageFile, galleryPhotoFiles, seoKeywords, street, houseNumber, postalCode, city, country, ...rest } = p as RestaurantCreateRequest & { name?: string; latitude?: number; longitude?: number };
  const out = { ...rest, title: name ?? (p as { name?: string }).name } as Record<string, unknown>;
  delete out.latitude;
  delete out.longitude;
  // Nest address fields into address object for backend
  out.address = {
    street: street || '',
    streetNumber: houseNumber || undefined,
    postalCode: postalCode || '',
    city: city || '',
    country: country || '',
  };
  // Remove flat address fields that leaked via spread
  delete out.street;
  delete out.houseNumber;
  delete out.postalCode;
  delete out.city;
  delete out.country;
  // Convert comma-separated keywords string to array for backend
  if (seoKeywords && typeof seoKeywords === 'string') {
    out.seoKeywords = seoKeywords.split(',').map((k: string) => k.trim()).filter(Boolean);
  }
  return out;
}

/** Map frontend payload to backend for update: name->title, nest address, galleryPhotos->gallery, keywords string->array */
function toBackendUpdate(p: RestaurantUpdateRequest) {
  const { name, galleryPhotos, logoFile, coverImageFile, galleryPhotoFiles, seoKeywords, street, houseNumber, postalCode, city, country, ...rest } = p as RestaurantUpdateRequest & {
    name?: string;
    galleryPhotos?: string[];
    seoKeywords?: string;
  };
  const out = { ...rest } as Record<string, unknown>;
  if (name !== undefined) out.title = name;
  if (galleryPhotos !== undefined) out.gallery = galleryPhotos;
  // Nest address fields if any are provided
  if (street !== undefined || houseNumber !== undefined || postalCode !== undefined || city !== undefined || country !== undefined) {
    const addr: Record<string, unknown> = {};
    if (street !== undefined) addr.street = street;
    if (houseNumber !== undefined) addr.streetNumber = houseNumber;
    if (postalCode !== undefined) addr.postalCode = postalCode;
    if (city !== undefined) addr.city = city;
    if (country !== undefined) addr.country = country;
    out.address = addr;
  }
  // Remove flat address fields that leaked via spread
  delete out.street;
  delete out.houseNumber;
  delete out.postalCode;
  delete out.city;
  delete out.country;
  if (seoKeywords && typeof seoKeywords === 'string') {
    out.seoKeywords = seoKeywords.split(',').map((k: string) => k.trim()).filter(Boolean);
  }
  return out;
}

/** Coerce API value to boolean (backend may send storeOpen/deliveryOpen as boolean or string). */
function toBoolean(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
  if (typeof value === 'number') return value !== 0;
  return Boolean(value);
}

/** Map backend response to frontend: restaurantId->id, title->name, gallery->galleryPhotos, storeOpen->isStoreOpen, deliveryOpen->isDeliveryOpen, closed->isClosed, closeMessage, deliveryZones. Supports nested address: { street, streetNumber, postalCode, city, country }. */
function fromBackend(raw: Record<string, unknown>): RestaurantResponse {
  const { restaurantId, title, gallery, storeOpen, deliveryOpen, closed, isClosed: isClosedRaw, closeMessage, deliveryZones, ...rest } = raw;
  const storeOpenVal = storeOpen ?? raw.isStoreOpen;
  const deliveryOpenVal = deliveryOpen ?? raw.isDeliveryOpen;
  const isClosedVal = closed ?? isClosedRaw;
  const addr = raw.address && typeof raw.address === 'object' ? (raw.address as Record<string, unknown>) : null;
  return {
    ...rest,
    id: (restaurantId ?? raw.id) as string,
    name: (title ?? raw.name) as string,
    galleryPhotos: (gallery ?? raw.galleryPhotos) as string[] | undefined,
    isStoreOpen: toBoolean(storeOpenVal),
    isDeliveryOpen: toBoolean(deliveryOpenVal),
    isClosed: toBoolean(isClosedVal),
    closeMessage: (closeMessage ?? raw.closeMessage) as string | undefined,
    deliveryZones: Array.isArray(deliveryZones) ? deliveryZones as RestaurantResponse['deliveryZones'] : (raw.deliveryZones as RestaurantResponse['deliveryZones'] | undefined),
    street: (addr?.street ?? raw.street) as string | undefined,
    streetNumber: (addr?.streetNumber ?? raw.streetNumber) as string | undefined,
    houseNumber: (addr?.streetNumber ?? raw.houseNumber ?? raw.streetNumber) as string | undefined,
    postalCode: (addr?.postalCode ?? raw.postalCode) as string | undefined,
    city: (addr?.city ?? raw.city) as string | undefined,
    country: (addr?.country ?? raw.country) as string | undefined,
    ownerName: raw.ownerName as string | undefined,
    taxNumber: raw.taxNumber as string | undefined,
    vatNumber: raw.vatNumber as string | undefined,
    businessNumber: raw.businessNumber as string | undefined,
    // Legal / policy (top-level optional fields)
    impressum: raw.impressum as string | undefined,
    datenschutz: raw.datenschutz as string | undefined,
    agb: raw.agb as string | undefined,
    widerruf: raw.widerruf as string | undefined,
    cookie: raw.cookie as string | undefined,
    odr: raw.odr as string | undefined,
    alkohol: raw.alkohol as string | undefined,
    legalPages: raw.legalPages as Record<string, string> | undefined,
    deliveryTimeRanges: Array.isArray(raw.deliveryTimeRanges) ? (raw.deliveryTimeRanges as RestaurantResponse['deliveryTimeRanges']) : undefined,
    pickupTimeRanges: Array.isArray(raw.pickupTimeRanges) ? (raw.pickupTimeRanges as RestaurantResponse['pickupTimeRanges']) : undefined,
    stripePaymentMode: (raw.stripePaymentMode as 'test' | 'prod') ?? undefined,
  } as RestaurantResponse;
}

function joinUrl(base: string, path: string) {
  if (!base) return path;
  if (base.endsWith('/') && path.startsWith('/')) return base + path.slice(1);
  if (!base.endsWith('/') && !path.startsWith('/')) return `${base}/${path}`;
  return base + path;
}

/** Extract user-friendly message from backend error response. */
function getUploadErrorMessage(res: Response, err: Record<string, unknown>): string {
  const msg = (err?.message ?? err?.error) as string | undefined;
  if (msg) {
    if (msg.includes('project was not found') || msg.includes('404 Not Found')) {
      return 'Image storage is not configured. Please check your server setup.';
    }
    if (msg.includes('Maximum upload size exceeded') || msg.includes('exceeds its maximum')) {
      return 'File is too large. Maximum 10MB per image.';
    }
    if (msg.length > 150) return msg.slice(0, 150) + '…';
    return msg;
  }
  if (res.status === 413) return 'File is too large. Maximum 10MB per image.';
  if (res.status === 404) return 'Restaurant or storage not found.';
  if (res.status === 401) return 'Session expired. Please log in again.';
  return `Upload failed (${res.status})`;
}

/**
 * Restaurant API client - Owner API for authenticated restaurant owners.
 * Backend: /api/owner/restaurants
 */
export const restaurantApi = {
  /**
   * Create a new restaurant.
   * POST /api/owner/restaurants
   */
  async createRestaurant(
    token: string | null,
    payload: RestaurantCreateRequest
  ): Promise<RestaurantResponse> {
    if (!token) throw new Error('No token');
    const body = toBackendCreate(payload);
    const raw = await apiFetchData<Record<string, unknown>>(
      OWNER_RESTAURANTS,
      { method: 'POST', headers: withAuthHeader(undefined, token), body },
      getAuthRetry()
    );
    return fromBackend(raw);
  },

  /**
   * Update restaurant.
   * PUT /api/owner/restaurants/{restaurantId}
   */
  async updateRestaurant(
    token: string | null,
    id: string,
    payload: RestaurantUpdateRequest
  ): Promise<RestaurantResponse> {
    if (!token) throw new Error('No token');
    const body = toBackendUpdate(payload);
    const raw = await apiFetchData<Record<string, unknown>>(
      `${OWNER_RESTAURANTS}/${id}`,
      { method: 'PUT', headers: withAuthHeader(undefined, token), body },
      getAuthRetry()
    );
    return fromBackend(raw);
  },

  /**
   * Get all restaurants for the authenticated user.
   * GET /api/owner/restaurants
   */
  async getRestaurants(token: string | null): Promise<RestaurantResponse[]> {
    if (!token) throw new Error('No token');
    const list = await apiFetchData<Record<string, unknown>[]>(
      OWNER_RESTAURANTS,
      { method: 'GET', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
    return (Array.isArray(list) ? list : []).map(fromBackend);
  },

  /**
   * Get a specific restaurant.
   * GET /api/owner/restaurants/{restaurantId}
   */
  async getRestaurant(token: string | null, id: string): Promise<RestaurantResponse> {
    if (!token) throw new Error('No token');
    const raw = await apiFetchData<Record<string, unknown>>(
      `${OWNER_RESTAURANTS}/${id}`,
      { method: 'GET', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
    return fromBackend(raw);
  },

  // ==================== IMAGE UPLOADS ====================

  /**
   * Upload restaurant logo.
   * POST /api/owner/restaurants/{restaurantId}/logo
   */
  async uploadLogo(
    token: string | null,
    restaurantId: string,
    file: File
  ): Promise<{ imageUrl: string; message: string }> {
    if (!token) throw new Error('No token');
    const formData = new FormData();
    formData.append('file', file);
    const url = joinUrl(config.apiBaseUrl, `${OWNER_RESTAURANTS}/${restaurantId}/logo`);
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
      credentials: 'include',
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      throw new Error(getUploadErrorMessage(res, err));
    }
    return res.json();
  },

  /**
   * Upload restaurant cover/banner image.
   * POST /api/owner/restaurants/{restaurantId}/cover
   */
  async uploadCover(
    token: string | null,
    restaurantId: string,
    file: File
  ): Promise<{ imageUrl: string; message: string }> {
    if (!token) throw new Error('No token');
    const formData = new FormData();
    formData.append('file', file);
    const url = joinUrl(config.apiBaseUrl, `${OWNER_RESTAURANTS}/${restaurantId}/cover`);
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
      credentials: 'include',
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      throw new Error(getUploadErrorMessage(res, err));
    }
    return res.json();
  },

  /**
   * Upload gallery images (replaces entire gallery).
   * POST /api/owner/restaurants/{restaurantId}/gallery
   */
  async uploadGallery(
    token: string | null,
    restaurantId: string,
    files: File[]
  ): Promise<{ imageUrl?: string; imageUrls?: string[]; message: string }> {
    if (!token) throw new Error('No token');
    if (!files.length) throw new Error('At least one file required');
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    const url = joinUrl(config.apiBaseUrl, `${OWNER_RESTAURANTS}/${restaurantId}/gallery`);
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
      credentials: 'include',
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      throw new Error(getUploadErrorMessage(res, err));
    }
    return res.json();
  },

  /**
   * Delete restaurant logo.
   * DELETE /api/owner/restaurants/{restaurantId}/logo
   */
  async deleteLogo(token: string | null, restaurantId: string): Promise<void> {
    if (!token) throw new Error('No token');
    await apiFetchData<unknown>(`${OWNER_RESTAURANTS}/${restaurantId}/logo`, {
      method: 'DELETE',
      headers: withAuthHeader(undefined, token),
    });
  },

  /**
   * Delete restaurant cover image.
   * DELETE /api/owner/restaurants/{restaurantId}/cover
   */
  async deleteCover(token: string | null, restaurantId: string): Promise<void> {
    if (!token) throw new Error('No token');
    await apiFetchData<unknown>(`${OWNER_RESTAURANTS}/${restaurantId}/cover`, {
      method: 'DELETE',
      headers: withAuthHeader(undefined, token),
    });
  },

  /**
   * Delete a single gallery image.
   * DELETE /api/owner/restaurants/{restaurantId}/gallery?imageUrl={url}
   */
  async deleteGalleryImage(token: string | null, restaurantId: string, imageUrl: string): Promise<void> {
    if (!token) throw new Error('No token');
    const path = `${OWNER_RESTAURANTS}/${restaurantId}/gallery?imageUrl=${encodeURIComponent(imageUrl)}`;
    await apiFetchData<unknown>(path, {
      method: 'DELETE',
      headers: withAuthHeader(undefined, token),
    });
  },

  /**
   * Delete all gallery images.
   * DELETE /api/owner/restaurants/{restaurantId}/gallery/all
   */
  async deleteGalleryAll(token: string | null, restaurantId: string): Promise<void> {
    if (!token) throw new Error('No token');
    await apiFetchData<unknown>(`${OWNER_RESTAURANTS}/${restaurantId}/gallery/all`, {
      method: 'DELETE',
      headers: withAuthHeader(undefined, token),
    });
  },

  /**
   * Delete a restaurant.
   * DELETE /api/owner/restaurants/{restaurantId}
   */
  async deleteRestaurant(token: string | null, restaurantId: string): Promise<void> {
    if (!token) throw new Error('No token');
    await apiFetchData<unknown>(`${OWNER_RESTAURANTS}/${restaurantId}`, {
      method: 'DELETE',
      headers: withAuthHeader(undefined, token),
    });
  },

  // ==================== CUSTOM DOMAIN ====================

  async submitDomain(token: string | null, restaurantId: string, domain: string): Promise<DomainStatusResponse> {
    if (!token) throw new Error('No token');
    return apiFetchData<DomainStatusResponse>(
      `${OWNER_RESTAURANTS}/${restaurantId}/domain`,
      { method: 'POST', headers: withAuthHeader(undefined, token), body: { domain } },
      getAuthRetry()
    );
  },

  async getDomainStatus(token: string | null, restaurantId: string): Promise<DomainStatusResponse> {
    if (!token) throw new Error('No token');
    return apiFetchData<DomainStatusResponse>(
      `${OWNER_RESTAURANTS}/${restaurantId}/domain`,
      { method: 'GET', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
  },

  async verifyDomain(token: string | null, restaurantId: string): Promise<DomainStatusResponse> {
    if (!token) throw new Error('No token');
    return apiFetchData<DomainStatusResponse>(
      `${OWNER_RESTAURANTS}/${restaurantId}/domain/verify`,
      { method: 'POST', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
  },

  async checkDomainDns(token: string | null, restaurantId: string): Promise<DnsCheckResponse> {
    if (!token) throw new Error('No token');
    return apiFetchData<DnsCheckResponse>(
      `${OWNER_RESTAURANTS}/${restaurantId}/domain/dns-check`,
      { method: 'GET', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
  },

  async removeDomain(token: string | null, restaurantId: string): Promise<void> {
    if (!token) throw new Error('No token');
    await apiFetchData<unknown>(
      `${OWNER_RESTAURANTS}/${restaurantId}/domain`,
      { method: 'DELETE', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
  },

  // ==================== LEGACY / ADAPTERS ====================
  // These methods adapt to backend - call the main endpoints with transformed payloads

  /** Alias for createRestaurant with minimal payload (name + slug). Backend accepts full CreateRestaurantRequest. */
  async startOnboarding(
    token: string | null,
    request?: { name: string; slug: string }
  ): Promise<RestaurantResponse> {
    if (!token) throw new Error('No token');
    if (!request?.name || !request?.slug) throw new Error('name and slug required');
    const payload: RestaurantCreateRequest = {
      name: request.name,
      slug: request.slug,
      description: request.name,
      contactEmail: '',
      contactPhone: '',
      street: '',
      postalCode: '',
      city: '',
      country: '',
      ownerName: request.name,
      taxNumber: '',
      businessNumber: '',
      currency: 'EUR',
      hasPickup: true,
      hasDelivery: false,
      openingHours: [] as DayHours[],
      isStoreOpen: true,
      isDeliveryOpen: false,
    };
    return this.createRestaurant(token, payload);
  },

  /** Multipart not supported by backend - uses createRestaurant with URLs only. */
  async createRestaurantMultipart(
    token: string | null,
    payload: RestaurantCreateRequest
  ): Promise<RestaurantResponse> {
    return this.createRestaurant(token, payload);
  },

  /** Multipart not supported by backend - uses updateRestaurant with URLs only. */
  async updateRestaurantMultipart(
    token: string | null,
    id: string,
    payload: RestaurantUpdateRequest
  ): Promise<RestaurantResponse> {
    return this.updateRestaurant(token, id, payload);
  },

  /** Settings merged into update - PATCH restaurant with settings payload. */
  async updateSettings(
    token: string | null,
    id: string,
    payload: { socialLinks?: Record<string, string>; languagesSupported?: string[]; paymentMethods?: string[] }
  ): Promise<RestaurantResponse> {
    return this.updateRestaurant(token, id, payload as RestaurantUpdateRequest);
  },

  /** Backend has no deleted endpoint - returns empty. */
  async getDeletedRestaurants(_token: string | null): Promise<RestaurantResponse[]> {
    return [];
  },

  /** Backend has no permanent delete - same as deleteRestaurant. */
  async permanentlyDeleteRestaurant(token: string | null, restaurantId: string): Promise<void> {
    return this.deleteRestaurant(token, restaurantId);
  },
};
